<!-- ErrorMessageDisplay.vue -->
<template>
  <div v-if="isVisible" class="error-modal">
    <div class="error-modal-content">
      <i @click="closeModal" class="fa-solid fa-xmark close-x-button"></i>

      <h3 class="errortitle">
        <i class="fa-solid fa-triangle-exclamation triangle"></i>
        Attenzione
      </h3>

      <!-- SCROLLABILE: solo gli errori normali (senza il blocco giallo) -->
      <div class="error-messages-scrollable">
        <div class="error-messages-container">
          <div
            v-for="section in groupedErrorSections"
            :key="section.key"
            class="error-section"
          >
            <p v-if="section.title" class="error-section-title">
              {{ section.title }}
            </p>

            <div
              v-for="(errorGroup, index) in section.groups"
              :key="section.key + '-' + index"
              :class="['error-group', errorGroup.type]"
            >
              <!-- TITOLO -->
              <p
                class="error-group-title"
                :class="{ 'na-title': errorGroup.type === 'combined-warning' }"
              >
                {{ errorGroup.title }}
              </p>

              <!-- LISTA MESSAGGI -->
              <ul
                class="error-list"
                :class="{ 'na-list': errorGroup.type === 'combined-warning' }"
              >
                <li
                  v-for="(msg, msgIndex) in errorGroup.messages"
                  :key="msgIndex"
                >
                  {{ msg }}
                </li>
              </ul>

              <!-- QUI NON C'È PIÙ na-actions -->
            </div>
          </div>
        </div>
      </div>

      <!-- BLOCCO GIALLO FISSO (fuori dallo scroll) -->
      <div v-if="hasCombinedWarning" class="combined-warning-fixed">
        <div
          v-for="errorGroup in errors"
          :key="'fixed-warning-' + errors.indexOf(errorGroup)"
        >
          <div v-if="errorGroup.type === 'combined-warning'" class="na-actions">
            <p class="na-question" v-html="errorGroup.question"></p>
            <div class="na-buttons">
              <button @click="$emit('close')" class="btn-cancel">
                Torna indietro
              </button>
              <button @click="$emit('proceed-warnings')" class="btn-proceed">
                Prosegui
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- PULSANTI PRINCIPALI (sempre in fondo) -->
      <div v-if="!hasCombinedWarning" class="modal-actions">
        <!-- Contenitore flex per centrare tutto -->
        <div class="modal-actions-buttons">
          <!-- Pulsante riempimento (solo se ci sono celle vuote) -->
          <div v-if="hasEmptyCells" class="fill-na-wrapper">
            <button @click="fillAllEmpty" :class="fillButtonClass">
              {{ fillButtonText }}
            </button>
            <div class="info-tooltip-na">
              <i class="fa-solid fa-circle-info info-icon-na"></i>
              <span class="tooltip-text" v-html="fillTooltipText"></span>
            </div>
          </div>

          <!-- Pulsante Annulla – sempre presente qui sotto -->
          <button @click="closeModal" class="error-modal-close-button">
            Annulla
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ErrorMessageDisplay",
  props: {
    errors: { type: Array, default: () => [] },
    isVisible: { type: Boolean, default: false },
  },
  computed: {
    groupedErrorSections() {
      const sections = [];
      const sectionMap = new Map();

      this.errors.forEach((errorGroup, index) => {
        const title = errorGroup.section || "";
        const key = title || `default-${index}`;

        if (!title) {
          sections.push({
            key,
            title: "",
            groups: [errorGroup],
          });
          return;
        }

        if (!sectionMap.has(title)) {
          const section = {
            key: title,
            title,
            groups: [],
          };
          sectionMap.set(title, section);
          sections.push(section);
        }

        sectionMap.get(title).groups.push(errorGroup);
      });

      return sections;
    },
    hasEmptyCodContoCells() {
      return this.errors.some(
        (e) => e.title === "Codici Conto mancanti:" && e.messages.length > 0
      );
    },
    hasEmptyDescriptions() {
      return this.errors.some(
        (e) =>
          e.title === "Descrizioni Codici Conto mancanti:" &&
          e.messages.length > 0
      );
    },
    hasEmptyProspettoPaghe() {
      return this.errors.some(
        (e) =>
          e.title === "Descrizioni nel Prospetto Paghe mancanti:" &&
          e.messages.length > 0
      );
    },
    hasCombinedWarning() {
      return this.errors.some((e) => e.type === "combined-warning");
    },
    hasEmptyCells() {
      return (
        this.hasEmptyCodContoCells ||
        this.hasEmptyDescriptions ||
        this.hasEmptyProspettoPaghe
      );
    },

    fillButtonText() {
      const hasCodici = this.hasEmptyCodContoCells;
      const hasDescrConto = this.hasEmptyDescriptions; // Descr Cod Conto / 2
      const hasProspetto = this.hasEmptyProspettoPaghe; // Descr nel Prospetto Paghe

      if (hasProspetto && !hasCodici && !hasDescrConto) {
        return "Inserisci le descrizioni obbligatorie";
      }
      if (hasProspetto && (hasCodici || hasDescrConto)) {
        return "Riempi celle mancanti";
      }
      if (hasCodici && hasDescrConto) {
        return "Riempi celle vuote";
      }
      if (hasCodici) {
        return "Riempi con N.A.";
      }
      if (hasDescrConto) {
        return 'Assegna "NESSUNA CORRELAZIONE"';
      }
      return "Riempi celle vuote";
    },

    fillButtonClass() {
      const hasCodici = this.hasEmptyCodContoCells;
      const hasDescrConto = this.hasEmptyDescriptions;
      const hasProspetto = this.hasEmptyProspettoPaghe;

      if (hasProspetto && !hasCodici && !hasDescrConto) {
        return "fill-required-button"; // puoi creare uno stile diverso, es. rosso
      }
      if (hasProspetto || (hasCodici && hasDescrConto)) {
        return "fill-combined-button";
      }
      if (hasCodici) {
        return "fill-na-button";
      }
      return "fill-no-corr-button";
    },

    fillTooltipText() {
      const hasCodici = this.hasEmptyCodContoCells;
      const hasDescrConto = this.hasEmptyDescriptions;
      const hasProspetto = this.hasEmptyProspettoPaghe;

      if (hasProspetto && !hasCodici && !hasDescrConto) {
        return `
      La colonna <strong>Descrizione nel Prospetto Paghe</strong> è <strong>obbligatoria</strong>.<br>
      Devi inserirla manualmente per ogni riga evidenziata.<br><br>
      Non può essere lasciata vuota né riempita automaticamente.
    `.trim();
      }

      if (hasProspetto && (hasCodici || hasDescrConto)) {
        return `
      Alcune descrizioni obbligatorie sono mancanti.<br>
      Inserisci manualmente i valori nella colonna <strong>Descrizione nel Prospetto Paghe</strong>.<br><br>
      Le altre celle vuote verranno riempite con N.A. o "NESSUNA CORRELAZIONE".
    `.trim();
      }

      if (hasCodici && hasDescrConto) {
        return `
      Riempirà i Codici Conto vuoti con <strong>'N.A.'</strong><br>
      e le descrizioni vuote con <strong>'NESSUNA CORRELAZIONE'</strong>.
    `.trim();
      }

      if (hasCodici) {
        return `
      Inserisci 'N.A.' per i Codici Conto che non vuoi compilare.<br><br>
      Utile per saltare temporaneamente un gestionale o un Conto non utilizzato.
    `.trim();
      }

      if (hasDescrConto) {
        return `
      Inserisce 'NESSUNA CORRELAZIONE' in tutte le descrizioni Cod. Conto vuote.<br><br>
      Utile per saltare temporaneamente un conto che non ha corrispondenza.
    `.trim();
      }

      return "";
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    fillAllEmpty() {
      if (this.hasEmptyCodContoCells) this.$emit("fill-na");
      if (this.hasEmptyDescriptions || this.hasEmptyProspettoPaghe)
        this.$emit("fill-no-corr");
    },
  },
};
</script>

<style scoped>
/* TUTTO IL TUO CSS ORIGINALE RIMANE IDENTICO */
.error-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9900;
}
.error-modal-close-button {
  padding: 10px 20px;
  font-size: 0.875rem;
  min-width: 120px;
  justify-content: center;
  font-weight: 700;
  background-color: white;
  color: #1474bd;
  border-style: solid;
  border-radius: 0.125rem;
  border-color: #1474bd;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: inline-flex;
  align-items: center;
  outline: none;
}
.error-modal-close-button:hover {
  color: #0d4d7d;
  border-color: #0d4d7d;
}

.modal-actions {
  width: 100%;
  padding: 20px 25px;
  box-sizing: border-box;
  border-top: 1px solid #ddd;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}
.modal-actions-buttons {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
}
modal-actions-buttons:has(.fill-na-wrapper) {
  justify-content: center;
}

.modal-actions-buttons:not(:has(.fill-na-wrapper)) {
  justify-content: center;
}

/* MODIFICHE MINIME SOLO QUI */
.error-modal-content {
  position: relative;
  background: white;
  border: 1px solid #d9dde3;
  border-radius: 8px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
  max-width: 680px;
  width: 90%;
  max-height: 82vh; /* più alto per mobile */
  text-align: left;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.error-modal-content h3 {
  text-align: center;
  margin: 0;
  padding: 1.2rem 2rem;
  font-size: 1.25rem;
  background: #f2f2f1;
  border-bottom: 1px solid #e2e5e9;
  /* color: #1d683a; */
}

.close-x-button {
  position: absolute;
  top: 20px;
  right: 30px;
  background: transparent;
  font-size: 1.6rem;
  font-weight: bold;
  color: #777;
}
.close-x-button:hover {
  color: #8b8b8b;
}
.center-single-button {
  justify-content: center;
}
/* SCROLL SOLO SULLA LISTA */
.error-messages-scrollable {
  flex: 1;
  overflow-y: auto;
}
.error-messages-container {
  padding: 1rem 1.75rem 1.25rem;
}

/* TUTTO IL RESTO IDENTICO AL TUO */
.error-section {
  padding: 0.9rem 0 1rem;
  border-bottom: 1px solid #e6e6e6;
}
.error-section:last-child {
  border-bottom: 0;
}
.error-section-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.7rem;
  color: #36404a;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.error-section-title::before {
  content: "";
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: #1474bd;
}
.error-group + .error-group {
  margin-top: 0.9rem;
}
.error-group-title {
  font-weight: bold;
  font-size: 1rem;
  margin: 0 0 5px;
  color: #030303;
}
.error-list {
  list-style: none;
  padding-left: 10px;
  margin: 0;
}
.error-list li {
  color: #555;
  font-size: 0.95em;
  line-height: 1.4;
  margin-bottom: 5px;
  padding-left: 0.5rem;
}
.empty-cells .error-list li,
.format-errors .error-list li {
  color: #000;
}
.triangle {
  margin-right: 5px;
  /* color: #1d683a; */
  font-size: 1.4rem;
}
.fill-na-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  /* align-items: center; */
}

.fill-na-button,
.fill-no-corr-button,
.fill-combined-button {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: auto;
  padding: 10px 20px;
  outline: none;
  border-radius: 0.125rem;
  border-style: solid;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: inherit;
  color: white;
  background-color: #1474bd;
  border-color: #1474bd;
}
.fill-na-button:hover,
.fill-no-corr-button:hover,
.fill-combined-button:hover {
  background-color: #115e9a;
  border-color: #0f558b;
  color: #f9f9f9;
}

.info-tooltip-na {
  display: inline-block;
  position: relative;
  height: 1rem;
}
.info-icon-na {
  color: #115e9a;
  font-size: 17px;
  transition: color 0.2s;
}
.tooltip-text {
  visibility: hidden;
  opacity: 0;
  min-width: 365px;
  background-color: #2c3e50;
  color: white;
  text-align: left;
  border-radius: 8px;
  padding: 14px 16px;
  position: absolute;
  z-index: 9999;
  bottom: 160%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
  line-height: 1.5;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: opacity 0.3s, visibility 0.3s;
  pointer-events: none;
}
.tooltip-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -8px;
  border: 8px solid transparent;
  border-top-color: #2c3e50;
}
.info-tooltip-na:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.na-title {
  color: #565656 !important;
  background-color: #f2f2f100;
  padding: 0px 12px;
  border-radius: 6px;
  font-weight: 600;
  margin-bottom: 8px;
}
.na-list {
  background-color: #f2f2f100;
  padding: 10px;
  border-radius: 6px;
  margin: 8px 0;
  /* border-left: 2px solid #959595; */
  font-size: 0.95em;
}
.na-actions {
  margin-top: 15px;
  text-align: center;
}
.na-question {
  font-size: 0.95em;
  color: #5a5a5a;
  margin-bottom: 15px;
  font-weight: 500;
  padding: 0 3rem;
  text-align: left;
}
.na-buttons {
  width: 100%;
  padding: 16px 25px;
  box-sizing: border-box;
  border-top: 1px solid #ddd;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
}
.na-buttons button {
  width: 10rem;
}
.btn-proceed {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: -moz-fit-content;
  width: fit-content;
  height: auto;
  padding: 10px 20px;
  outline: none;
  border-radius: 0.125rem;
  border-style: solid;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: inherit;
  color: white;
  background-color: #1474bd;
  border-color: #1474bd;
}
.btn-proceed:hover {
  background-color: #115e9a;
  border-color: #0f558b;
  color: #f9f9f9;
}
.btn-cancel {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: -moz-fit-content;
  width: fit-content;
  height: auto;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  border-radius: 0.125rem;
  border-style: solid;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: inherit;
  color: white;
  background-color: #8c8c8c;
  border-color: #8c8c8c;
}
.btn-cancel:hover {
  background-color: #7a7a7a;
  border-color: #767676;
  color: #f9f9f9;
}
.info-icon-na2 {
  color: #686868;
  margin-right: 6px;
  font-size: 1.1em;
}
</style>
